//Built-in libraries

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
//Third party libraries
import { Controller, get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookCheckboxGroup } from '@/components/UI/HookForm/Form/CheckBoxGroup';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { type UploadFnReturn, useFileUpload } from '@/hooks/useFileUpload';

//STP Libraries

import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import { HookRadioGroup } from '@/components/UI/HookForm/Form/RadioGroup';
import type { SignedApplicationAssetResponse } from '@/types/Shared';
import { compareTwoArrays } from '@/utils';
//Assets and Miscellaneous
import { majorityOwnership, racialEthnicGroup } from './constant';

//Component Begin

export default function LeadershipInformation() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');
  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const {
    trigger,
    getValues,
    watch,
    control,
    formState: { errors, isValid },
  } = useFormContext();

  const hasMajorityOwnership = watch('leadershipInformation.hasMajorityOwnership');

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const uploadFile = useMutation((payload: { signAssetPayload: ISignApplicationPublicAssetPayload[]; files: File[] }) => upload(payload), {
    onSuccess: ({ sign, files }: UploadFnReturn) => {
      let signRes: SignedApplicationAssetResponse[] = [];
      if (Array.isArray(sign)) {
        signRes = [...sign];
      } else {
        signRes = [{ ...sign }];
      }

      updateData({
        leadershipInformation: {
          ...getValues('leadershipInformation'),
          principalsResume: signRes.map((sig, index) => ({
            name: files[index].name,
            path: sig.path,
            key: sig.key,
            type: files[index].type,
            field: 'principalsResume',
            description: 'Principal Resume',
          })),
        },
      });
      setCurrentStep((prev) => prev + 1);
    },
    onError: (_error: any) => {
      setUploadError('Failed to upload Document');
    },
  });

  const onSubmit = () => {
    trigger();
    if (!isValid) {
      return;
    }

    const formData = getValues('leadershipInformation');

    if (formData.selectedFiles?.length) {
      const formDataSelectedFiles = formData?.selectedFiles.map((f: File) => f.name);
      const formContextDataUploadedFiles = formContextData?.leadershipInformation?.principalsResume.map((f: File) => f.name);

      const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

      if (fileUploadCompare.array1Diff.length > 0) {
        const signAssetPayload = formData.selectedFiles
          .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
          .map((file: File) => ({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            formId: String(params.applicationFormId),
          }));
        uploadFile.mutate({
          signAssetPayload,
          files: formData.selectedFiles,
        });
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    } else {
      updateData({
        leadershipInformation: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='space-y-6 sm:px-6 lg:px-0 lg:col-span-8'>
      <div className='sm:rounded-r-md sm:overflow-hidden'>
        <div className='py-6 px-4 space-y-6 sm:p-6'>
          <div>
            <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Leadership Information</h3>
            <p className='mt-1 text-sm text-gray-500'>Identify leadership of the company</p>
          </div>
          <div className='grid grid-cols-6 gap-6'>
            <h4 className='leading-6 font-medium text-gray-900 col-span-6'>Identify the ownership breakdown of the company</h4>

            <div className='leading-6 font-medium text-gray-900 col-span-6'>
              <HookCheckboxGroup
                label='What racial/ethnic groups does the highest authority member of
                  management (President, CEO, Partner, etc) belong to? Select
                  all that apply:'
                name='leadershipInformation.racialEthnicGroup'
                options={racialEthnicGroup.map((r) => ({
                  label: r.label,
                  value: r.label,
                }))}
              />
            </div>
            <HookRadioGroup
              className='col-span-6 md:col-span-6'
              label='Is the majority ownership (51% or greater) of the company held by someone that is considered disadvantaged?'
              name='leadershipInformation.hasMajorityOwnership'
              id='has-majority-ownership'
              options={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
            {hasMajorityOwnership === 'yes' && (
              <div className='leading-6 font-medium text-gray-900 col-span-6'>
                <HookCheckboxGroup
                  label=''
                  name='leadershipInformation.majorityOwnership'
                  options={majorityOwnership.map((m) => ({
                    label: m.label,
                    value: m.label,
                  }))}
                />
              </div>
            )}
            <div className='leading-6 font-medium text-gray-900 col-span-6'>
              <label htmlFor='board-of-directors-percent' className='block font-medium text-gray-900'>
                If the company is governed by a board of directors, please specify the percentage of the board that identifies with any of
                the groups below:
              </label>
              <fieldset id='board-of-directors-percent' className='space-y-2 mt-2'>
                <Controller
                  name='leadershipInformation.boardOfDirectorsPercent.communityMembersPercent'
                  control={control}
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                      <div className='ml-3 text-sm flex w-full gap-2'>
                        <label htmlFor='boardCommunityMembers' className='font-medium text-gray-700'>
                          Community Members
                        </label>
                        <input
                          type='number'
                          id='communityMembersPercent'
                          autoComplete='communityMembersPercent'
                          className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                          {...field}
                        />
                        <span>%</span>
                      </div>
                      {error?.message && <span className='ml-3 text-xs text-tertiaryColor-normal'>{error.message}</span>}
                    </>
                  )}
                />
                <Controller
                  name='leadershipInformation.boardOfDirectorsPercent.hispanicLatinxPercent'
                  control={control}
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                      <div className='ml-3 text-sm flex w-full gap-2'>
                        <label htmlFor='boardHispanicLatinx' className='font-medium text-gray-700'>
                          Hispanic/Latinx
                        </label>
                        <input
                          type='number'
                          id='hispanicLatinxPercent'
                          autoComplete='hispanicLatinxPercent'
                          className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                          {...field}
                        />
                        <span>%</span>
                      </div>
                      {error?.message && <span className='ml-3 text-xs text-tertiaryColor-normal'>{error.message}</span>}
                    </>
                  )}
                />
                <Controller
                  name='leadershipInformation.boardOfDirectorsPercent.blackAfricanAmericanPercent'
                  control={control}
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                      <div className='ml-3 text-sm flex w-full gap-2'>
                        <label htmlFor='boardBlackAfricanAmerican' className='font-medium text-gray-700'>
                          Black/African American (not Hispanic or Latinx)
                        </label>
                        <input
                          type='number'
                          id='blackAfricanAmericanPercent'
                          autoComplete='blackAfricanAmericanPercent'
                          className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                          {...field}
                        />
                        <span>%</span>
                      </div>
                      {error?.message && <span className='ml-3 text-xs text-tertiaryColor-normal'>{error.message}</span>}
                    </>
                  )}
                />

                <Controller
                  name='leadershipInformation.boardOfDirectorsPercent.asianPacificIslandersPercent'
                  control={control}
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                      <div className='ml-3 text-sm flex w-full gap-2'>
                        <label htmlFor='boardAsianPacificIslanders' className='font-medium text-gray-700'>
                          Asian American/Pacific Islanders/Asian (not Hispanic or Latinx)
                        </label>
                        <input
                          type='number'
                          id='asianPacificIslandersPercent'
                          autoComplete='asianPacificIslandersPercent'
                          className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                          {...field}
                        />
                        <span>%</span>
                      </div>
                      {error?.message && <span className='ml-3 text-xs text-tertiaryColor-normal'>{error.message}</span>}
                    </>
                  )}
                />
                <Controller
                  name='leadershipInformation.boardOfDirectorsPercent.nativeAmericanPercent'
                  control={control}
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                      <div className='ml-3 text-sm flex w-full gap-2'>
                        <label htmlFor='boardNativeAmerican' className='font-medium text-gray-700'>
                          Native American/American Indian/Alaska Native/Indigenous (not Hispanic or Latinx)
                        </label>
                        <input
                          type='number'
                          id='nativeAmericanPercent'
                          autoComplete='nativeAmericanPercent'
                          className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                          {...field}
                        />
                        <span>%</span>
                      </div>
                      {error?.message && <span className='ml-3 text-xs text-tertiaryColor-normal'>{error.message}</span>}
                    </>
                  )}
                />
                <Controller
                  name='leadershipInformation.boardOfDirectorsPercent.whiteCaucasianPercent'
                  control={control}
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                      <div className='ml-3 text-sm flex w-full gap-2'>
                        <label htmlFor='boardWhiteCaucasian' className='font-medium text-gray-700'>
                          White/Caucasian (not Hispanic or Latinx)
                        </label>
                        <input
                          type='number'
                          id='whiteCaucasianPercent'
                          autoComplete='whiteCaucasianPercent'
                          className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                          {...field}
                        />
                        <span>%</span>
                      </div>
                      {error?.message && <span className='ml-3 text-xs text-tertiaryColor-normal'>{error.message}</span>}
                    </>
                  )}
                />
                <Controller
                  name='leadershipInformation.boardOfDirectorsPercent.multiRacialPercent'
                  control={control}
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                      <div className='ml-3 text-sm flex w-full gap-2'>
                        <label htmlFor='boardMultiRacial' className='font-medium text-gray-700'>
                          Multi-Racial, 2+ races
                        </label>
                        <input
                          type='number'
                          id='multiRacialPercent'
                          autoComplete='multiRacialPercent'
                          className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                          {...field}
                        />
                        <span>%</span>
                      </div>
                      {error?.message && <span className='ml-3 text-xs text-tertiaryColor-normal'>{error.message}</span>}
                    </>
                  )}
                />
              </fieldset>
              {get(errors, 'leadershipInformation.boardOfDirectorsPercent') && (
                <p className='text-tertiaryColor-normal text-md ml-3 mt-4'>
                  {get(errors, 'leadershipInformation.boardOfDirectorsPercent')?.message}
                </p>
              )}
            </div>
          </div>
          <div className='col-span-6'>
            <HookFileInput
              name='leadershipInformation.selectedFiles'
              label='Principal Resume'
              id='selectedFiles'
              placeholder='Upload Principal Resume'
              multiple
            />
          </div>
          {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
          <div className='flex gap-4 justify-end mt-5'>
            <Button
              label='Previous'
              disabled={uploadFile.isLoading}
              btnStyle='btn-neutral'
              type='button'
              onClick={() => setCurrentStep((prev) => prev - 1)}
            />
            <Button
              label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
              btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
              disabled={uploadFile.isLoading}
              type='button'
              onClick={onSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
